'use client';

import { createClient } from 'utils/supabase/client';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

export default function ForkFunctionContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const functionName = searchParams.get('functionName');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAuthAndFork = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const supabase = createClient();
        const {
          data: { session }
        } = await supabase.auth.getSession();

        // If not authenticated, redirect to login with next parameter
        if (!session) {
          const currentUrl = `/fork_function${functionName ? `?functionName=${functionName}` : ''}`;
          router.push(`/login?next=${encodeURIComponent(currentUrl)}`);
          return;
        }

        if (!functionName) {
          toast.error('No function name provided');
          router.push('/dashboard');
          return;
        }

        const formData = new FormData();
        formData.append('functionName', functionName);

        const response = await fetch('/api/fork_function', {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fork function');
        }

        const data = await response.json();

        if (data.success && data.redirectUrl) {
          toast.success('Function forked successfully');
          router.push(data.redirectUrl);
        } else if (data.redirectUrl) {
          router.push(data.redirectUrl);
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('Error forking function:', error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthAndFork();
  }, [functionName, router]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-red-600">
            Error Forking Function
          </h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => router.push('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">
          {isLoading ? `Forking function: ${functionName}` : 'Processing...'}
        </h1>
        <p className="text-gray-600">
          {isLoading
            ? 'Please wait while we process your request.'
            : 'Redirecting...'}
        </p>
        {isLoading && (
          <div className="mt-4 w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        )}
      </div>
    </div>
  );
}
